import React from "react";
import { Link } from "react-router-dom";

/**
 * Shared article card — the single listing component used across the
 * Blog: main /blog grid, vertical hub "All articles" grids, and any
 * future category listings. Keep listings visually consistent by
 * reusing this component instead of writing bespoke cards.
 *
 * Fallback image:
 *   When an article does not carry a `featuredImage` yet (images are
 *   assigned editorially in this project), the card renders a
 *   deterministic branded placeholder in the same aspect-video slot
 *   so the grid stays uniform. The placeholder colour is derived from
 *   the article vertical (falls back to a neutral gradient) and the
 *   article's category or vertical name appears as a small eyebrow —
 *   never a fake photo, always honest about the content type.
 */

// Deterministic pastel palette per vertical slug — the same industry
// always gets the same colour block, which reinforces visual grouping
// when a reader scrolls a mixed grid.
const _VERTICAL_TINTS = {
  furniture:        { bg: "#f3ede4", fg: "#5a4634", accent: "#c89a67" },
  "building-materials": { bg: "#eceef1", fg: "#3a4658", accent: "#6b7a95" },
  "natural-stone":  { bg: "#ecebe8", fg: "#443f3a", accent: "#8a7f6f" },
  "wood-products":  { bg: "#efe7db", fg: "#5b4a30", accent: "#a2814d" },
  "textiles-and-fabrics": { bg: "#efe9ee", fg: "#4d3d4d", accent: "#8f6f8a" },
  "apparel-and-garments": { bg: "#e8efe9", fg: "#3d4d3f", accent: "#6a8a72" },
  "hospitality-products": { bg: "#ede7ee", fg: "#443949", accent: "#7a638f" },
  footwear:         { bg: "#efe8e2", fg: "#4d3f36", accent: "#8f6f52" },
  "leather-and-exotic-leather": { bg: "#ece5df", fg: "#4a3c33", accent: "#8f6647" },
};
const _DEFAULT_TINT = { bg: "#f4f4f5", fg: "#404040", accent: "#737373" };

function _tintFor(article) {
  const v = article?.vertical;
  const slug = typeof v === "object" ? v?.slug : v;
  return _VERTICAL_TINTS[slug] || _DEFAULT_TINT;
}

function _eyebrowText(article) {
  const v = article?.vertical;
  const vname = typeof v === "object" ? v?.name : null;
  const c = article?.category;
  const cname = typeof c === "object" ? c?.name : null;
  return vname || cname || "Article";
}

export default function ArticleCard({ article }) {
  const img = article?.featuredImage?.url || article?.seo?.ogImage?.url;
  const tint = _tintFor(article);
  const eyebrow = _eyebrowText(article);

  return (
    <Link
      to={`/blog/${article.slug}`}
      className="group flex flex-col h-full border border-neutral-200 rounded-lg overflow-hidden hover:border-neutral-900 transition-colors bg-white"
      data-testid={`article-card-${article.slug}`}
    >
      <div className="aspect-video overflow-hidden bg-neutral-100 relative">
        {img ? (
          <img
            src={img}
            alt={article?.featuredImage?.alt || article.title}
            className="w-full h-full object-cover group-hover:scale-[1.02] transition-transform"
            loading="lazy"
          />
        ) : (
          <div
            aria-hidden
            className="w-full h-full flex flex-col justify-between p-4"
            style={{ backgroundColor: tint.bg, color: tint.fg }}
            data-testid={`article-card-fallback-${article.slug}`}
          >
            <span className="text-[10px] uppercase tracking-[0.18em] font-medium opacity-80">
              {eyebrow}
            </span>
            <span
              className="text-[13px] font-semibold leading-tight line-clamp-3 pr-1"
              style={{ color: tint.fg }}
            >
              {article.title}
            </span>
            <span
              className="self-end h-[3px] w-10 rounded-full"
              style={{ backgroundColor: tint.accent, opacity: 0.85 }}
            />
          </div>
        )}
      </div>
      <div className="p-5 flex-1 flex flex-col">
        <h2 className="text-lg font-semibold text-neutral-900 line-clamp-2 group-hover:underline underline-offset-4">
          {article.title}
        </h2>
        {article.excerpt && (
          <p className="text-sm text-neutral-600 mt-2 line-clamp-3 leading-relaxed">
            {article.excerpt}
          </p>
        )}
      </div>
    </Link>
  );
}
